import { useEffect, useState } from 'react'
import { api, ApiError, type Card, type CardDetails, type Schema } from './api.js'

/**
 * Панель карточки: текст, метки, родословная, решения.
 *
 * Два правила интерфейса, которые следуют из ТЗ:
 *   — «Переделать» не отправляется без комментария;
 *   — «Выполнено» не отправляется без ссылки на публикацию.
 * Сервер это тоже проверяет — здесь просто не даём нажать зря.
 *
 * Конфликт правок (409) показываем прямо: файл изменился в Obsidian или его
 * правил Claude, и затирать чужое молча нельзя.
 */

export function CardPanel({
  path,
  schema,
  onClose,
  onChanged,
  onOpen,
}: {
  path: string
  schema: Schema
  onClose: () => void
  onChanged: () => void
  onOpen: (path: string) => void
}) {
  const [details, setDetails] = useState<CardDetails | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [body, setBody] = useState('')
  const [editing, setEditing] = useState(false)
  const [dialog, setDialog] = useState<'expand' | 'redo' | 'complete' | null>(null)

  async function load() {
    setError('')
    try {
      const data = await api.card(path)
      setDetails(data)
      setBody(data.body)
      setEditing(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось открыть карточку')
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  async function run(action: () => Promise<unknown>) {
    setBusy(true)
    setError('')
    try {
      await action()
      await load()
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  if (!details) {
    return (
      <aside className="panel">
        <header>
          <h2>{error ? 'Ошибка' : 'Загружаю…'}</h2>
          {error && <p className="error small">{error}</p>}
          <button className="small ghost" onClick={onClose}>Закрыть</button>
        </header>
      </aside>
    )
  }

  const { card } = details
  const stage = schema.stages.find((s) => s.ключ === card.stage)
  const format = schema.formats.find((f) => f.ключ === card.format)
  const archived = card.status === 'архив'

  return (
    <aside className="panel">
      <header>
        <h2>{card.title}</h2>
        <div className="meta row wrap">
          {stage && <span className="chip accent">{stage.название}</span>}
          {card.status && <span className="chip">{card.status}</span>}
          {format && <span className="chip">{format.название}</span>}
          {card.id && <span className="chip">{card.id}</span>}
          {card.lastEditor && <span className="chip">правил: {card.lastEditor}</span>}
        </div>
        {card.inferred.length > 0 && (
          <p className="small muted" style={{ margin: '8px 0 0' }}>
            Поля достроены автоматически: {card.inferred.join(', ')} — в файле их не было.
          </p>
        )}
      </header>

      <div className="body">
        {error && <p className="error small">{error}</p>}

        <div className="block">
          <h4>Текст</h4>
          {editing ? (
            <>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} />
              <div className="row" style={{ marginTop: 8 }}>
                <button
                  className="primary small"
                  disabled={busy}
                  onClick={() =>
                    void run(() => api.saveBody({ path, body, expectedMtimeMs: details.mtimeMs }))
                  }
                >
                  Сохранить
                </button>
                <button className="small ghost" onClick={() => { setBody(details.body); setEditing(false) }}>
                  Отменить
                </button>
              </div>
            </>
          ) : (
            <>
              <pre className="small" style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                {details.body.trim() || '— пусто —'}
              </pre>
              <button className="small ghost" style={{ marginTop: 8 }} onClick={() => setEditing(true)}>
                Править текст
              </button>
            </>
          )}
        </div>

        <Labels card={card} schema={schema} busy={busy} onSave={(labels) => run(() => api.saveLabels({ path, labels }))} />

        <div className="block lineage">
          <h4>Откуда пришло</h4>
          {card.sources.length === 0 && <p className="empty">источник не указан</p>}
          {card.sources.map((src) => (
            <a key={src} onClick={() => onOpen(wikilinkToPath(src, path))}>{src}</a>
          ))}
          {card.prototype && (
            <>
              <h4 style={{ marginTop: 12 }}>Прототип — чужая структура</h4>
              <a onClick={() => onOpen(wikilinkToPath(card.prototype!, path))}>{card.prototype}</a>
            </>
          )}
        </div>

        {details.children.length > 0 && (
          <div className="block lineage">
            <h4>Что из этого выросло</h4>
            {details.children.map((child) => (
              <a key={child.path} onClick={() => onOpen(child.path)}>
                {child.title} — {schema.formats.find((f) => f.ключ === child.format)?.название ?? child.kind}
              </a>
            ))}
          </div>
        )}

        {card.links.length > 0 && (
          <div className="block">
            <h4>Публикации</h4>
            {card.links.map((link) => (
              <a key={link} href={link} target="_blank" rel="noreferrer noopener" className="small">{link}</a>
            ))}
          </div>
        )}

        <div className="block">
          <h4>Файл в vault</h4>
          <p className="path">{card.path}</p>
        </div>
      </div>

      <footer>
        {archived ? (
          <button className="primary" disabled={busy} onClick={() => void run(() => api.restore(path))}>
            Вернуть в работу
          </button>
        ) : (
          <>
            {card.stage === 'идеи' && (
              <button className="primary" disabled={busy} onClick={() => setDialog('expand')}>
                Принять → в форматы
              </button>
            )}
            {card.stage === 'сценарий' && (
              <button
                className="primary"
                disabled={busy}
                onClick={() =>
                  format?.текстовый
                    ? setDialog('complete')
                    : void run(() => api.decide({ path, action: 'принять', expectedMtimeMs: details.mtimeMs }))
                }
              >
                {format?.текстовый ? 'Принять → выложено' : 'Принять → на съёмку'}
              </button>
            )}
            {(card.stage === 'съёмка' || card.stage === 'монтаж') && (
              <button
                className="primary"
                disabled={busy}
                onClick={() => void run(() => api.advance({ path, expectedMtimeMs: details.mtimeMs }))}
              >
                {card.stage === 'съёмка' ? 'Снято → на монтаж' : 'Смонтировано → выложить'}
              </button>
            )}
            {card.stage === 'монтаж' && (
              <button disabled={busy} onClick={() => setDialog('complete')}>Выложено — добавить ссылку</button>
            )}
            {stage?.решения && (
              <button disabled={busy} onClick={() => setDialog('redo')}>Переделать</button>
            )}
            {stage?.решения && (
              <button
                disabled={busy}
                onClick={() => void run(() => api.decide({ path, action: 'убрать', expectedMtimeMs: details.mtimeMs }))}
              >
                Убрать в архив
              </button>
            )}
            {card.stage === 'входящие' && (
              <button className="primary" disabled={busy} onClick={() => setDialog('expand')}>
                Сделать идею
              </button>
            )}
          </>
        )}
        <button className="ghost" onClick={onClose}>Закрыть</button>
      </footer>

      {dialog === 'expand' && card.stage === 'входящие' && (
        <IdeaFromIncoming
          sourcePath={path}
          expert={card.expert}
          onClose={() => setDialog(null)}
          onDone={(created) => { setDialog(null); onChanged(); onOpen(created) }}
        />
      )}
      {dialog === 'expand' && card.stage === 'идеи' && (
        <ExpandDialog
          path={path}
          schema={schema}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); void load(); onChanged() }}
        />
      )}
      {dialog === 'redo' && (
        <RedoDialog
          onClose={() => setDialog(null)}
          onDone={(comment) => {
            setDialog(null)
            void run(() => api.decide({ path, action: 'переделать', comment, expectedMtimeMs: details.mtimeMs }))
          }}
        />
      )}
      {dialog === 'complete' && (
        <CompleteDialog
          onClose={() => setDialog(null)}
          onDone={(links) => {
            setDialog(null)
            void run(() => api.complete({ path, links, expectedMtimeMs: details.mtimeMs }))
          }}
        />
      )}
    </aside>
  )
}

function Labels({
  card,
  schema,
  busy,
  onSave,
}: {
  card: Card
  schema: Schema
  busy: boolean
  onSave: (labels: { rubric?: string; direction?: string; method?: string }) => void
}) {
  const [rubric, setRubric] = useState(card.rubric ?? '')
  const [direction, setDirection] = useState(card.direction ?? '')
  const [method, setMethod] = useState(card.method ?? '')
  const dirty = rubric !== (card.rubric ?? '') || direction !== (card.direction ?? '') || method !== (card.method ?? '')

  useEffect(() => {
    setRubric(card.rubric ?? '')
    setDirection(card.direction ?? '')
    setMethod(card.method ?? '')
  }, [card.path, card.rubric, card.direction, card.method])

  return (
    <div className="block">
      <h4>Метки</h4>
      <div className="field" style={{ marginBottom: 9 }}>
        <label>Рубрика — как подано</label>
        <select value={rubric} onChange={(e) => setRubric(e.target.value)}>
          <option value="">— не выбрано —</option>
          {schema.rubrics.map((r) => (
            <option key={r.ключ} value={r.ключ}>{r.название}</option>
          ))}
        </select>
      </div>
      <div className="field" style={{ marginBottom: 9 }}>
        <label>Направление — про что</label>
        <select value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="">— не выбрано —</option>
          {Object.entries(schema.orbits).map(([orbit, label]) => (
            <optgroup key={orbit} label={label}>
              {schema.directions.filter((d) => d.орбита === orbit).map((d) => (
                <option key={d.ключ} value={d.ключ}>{d.название}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div className="field" style={{ marginBottom: 9 }}>
        <label>Методика — по какому подходу построено</label>
        <input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="например, Слом парадигмы (APAG)" />
      </div>
      {card.func && <p className="small muted" style={{ margin: '0 0 8px' }}>Функция контента: {card.func}</p>}
      <button
        className="small primary"
        disabled={busy || !dirty}
        onClick={() => onSave({ rubric: rubric || undefined, direction: direction || undefined, method })}
      >
        Сохранить метки
      </button>
    </div>
  )
}

function IdeaFromIncoming({
  sourcePath,
  expert,
  onClose,
  onDone,
}: {
  sourcePath: string
  expert: string | null
  onClose: () => void
  onDone: (created: string) => void
}) {
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  return (
    <Modal title="Новая идея из этого материала" onClose={onClose}>
      <div className="field">
        <label>Заголовок идеи</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </div>
      <div className="field">
        <label>Суть — в двух словах, детали допишет Claude</label>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} />
      </div>
      {error && <p className="error small">{error}</p>}
      <div className="row">
        <button
          className="primary"
          disabled={busy || !title.trim()}
          onClick={async () => {
            setBusy(true)
            try {
              const created = await api.createIdea({ title, text, sourcePath, expert: expert ?? undefined })
              onDone(created.path)
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Не получилось')
            } finally {
              setBusy(false)
            }
          }}
        >
          Создать идею
        </button>
        <button className="ghost" onClick={onClose}>Отмена</button>
      </div>
    </Modal>
  )
}

function ExpandDialog({
  path,
  schema,
  onClose,
  onDone,
}: {
  path: string
  schema: Schema
  onClose: () => void
  onDone: () => void
}) {
  const [formats, setFormats] = useState<string[]>([])
  const [rubric, setRubric] = useState('')
  const [direction, setDirection] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  return (
    <Modal title="Принять идею и развернуть в форматы" onClose={onClose}>
      <div className="field">
        <label>Форматы — на каждый будет своя карточка</label>
        <div className="checks">
          {schema.formats.map((f) => (
            <label key={f.ключ}>
              <input
                type="checkbox"
                checked={formats.includes(f.ключ)}
                onChange={(e) =>
                  setFormats((prev) => (e.target.checked ? [...prev, f.ключ] : prev.filter((x) => x !== f.ключ)))
                }
              />
              {f.название}
            </label>
          ))}
        </div>
      </div>
      <div className="field">
        <label>Рубрика — как подано</label>
        <select value={rubric} onChange={(e) => setRubric(e.target.value)}>
          <option value="">— выберите —</option>
          {schema.rubrics.map((r) => (
            <option key={r.ключ} value={r.ключ}>{r.название}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Направление — про что</label>
        <select value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="">— выберите —</option>
          {Object.entries(schema.orbits).map(([orbit, label]) => (
            <optgroup key={orbit} label={label}>
              {schema.directions.filter((d) => d.орбита === orbit).map((d) => (
                <option key={d.ключ} value={d.ключ}>{d.название}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {error && <p className="error small">{error}</p>}
      <div className="row">
        <button
          className="primary"
          disabled={busy || formats.length === 0 || !rubric || !direction}
          onClick={async () => {
            setBusy(true)
            try {
              await api.expand({ path, formats, rubric, direction })
              onDone()
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Не получилось')
            } finally {
              setBusy(false)
            }
          }}
        >
          Развернуть
        </button>
        <button className="ghost" onClick={onClose}>Отмена</button>
      </div>
    </Modal>
  )
}

function RedoDialog({ onClose, onDone }: { onClose: () => void; onDone: (comment: string) => void }) {
  const [comment, setComment] = useState('')
  return (
    <Modal title="Переделать" onClose={onClose}>
      <div className="field">
        <label>Что хорошо и что плохо — это задание Claude на следующий проход</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={6} autoFocus />
      </div>
      <div className="row">
        <button className="primary" disabled={!comment.trim()} onClick={() => onDone(comment.trim())}>
          Отправить на переделку
        </button>
        <button className="ghost" onClick={onClose}>Отмена</button>
      </div>
    </Modal>
  )
}

function CompleteDialog({ onClose, onDone }: { onClose: () => void; onDone: (links: string[]) => void }) {
  const [value, setValue] = useState('')
  const links = value.split(/\s+/).map((l) => l.trim()).filter(Boolean)
  const valid = links.length > 0 && links.every((l) => /^https?:\/\//i.test(l))
  return (
    <Modal title="Выложено" onClose={onClose}>
      <div className="field">
        <label>Ссылки на публикации — по одной в строке</label>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          placeholder="https://t.me/kuznitsa_profita/700"
          autoFocus
        />
        {value && !valid && <p className="error small">Ссылка должна начинаться с http:// или https://</p>}
      </div>
      <div className="row">
        <button className="primary" disabled={!valid} onClick={() => onDone(links)}>
          Сохранить и закрыть карточку
        </button>
        <button className="ghost" onClick={onClose}>Отмена</button>
      </div>
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  )
}

/**
 * Wikilink в путь файла. Ссылки в vault'е пишутся без расширения и без
 * корневой папки, поэтому корень берём из пути текущей карточки.
 */
function wikilinkToPath(link: string, currentPath: string): string {
  const inner = link.replace(/^\[\[/, '').replace(/\]\]$/, '').split('|')[0] ?? ''
  const root = currentPath.split('/')[0] ?? ''
  const withoutRoot = inner.startsWith(`${root}/`) ? inner.slice(root.length + 1) : inner
  return `${root}/${withoutRoot}.md`
}
